import _set from 'lodash/set';

/* eslint-disable @typescript-eslint/ban-types -- react-table is using `object`, so I have to follow that pattern here */

/**
 * Given an array of {@link URLParamFilter} objects, map it onto a {@link Filters} array. This
 * translates the data read from the URL search parameters into the next state of the react-table
 * filters, allowing the URL to control the filter state.
 *
 * If any of the filters have a value that is not just a simple string, you will need to pass the
 * appropriate empty filter state in the optional second argument, which is an array of
 * {@link Filter} objects that represent the empty state of a complex filter.
 *
 * @remarks
 * This mapping function has to handle a few special concerns:
 * 1. The `paramFilters` list can contain URL params that do not currently have a value in the URL;
 * i.e. have `paramValue === null`. These must be filtered from the output.
 * 2. The `paramFilters` list can include more than one filter value for a given column ID. This is
 * because column filters can take any shape. A good example is the `Program` column in the
 * PerformancesTable, which consists of both a `Show` and `Episode`. These strings must be declared
 * as separate URL search parameters but pulled together into a single Filter object that looks like:
 * ```
 *  {
 *      id: 'program',
 *      value: {
 *          Show: 'Foo',
 *          Episode: 'Bar',
 *      }
 *  }
 *```
 * ```
 * @param paramFilters - array of {@link URLParamFilter} objects derived from the URL
 * @param emptyFilterState - optional list of {@link Filter} objects that represent the empty state
 * for a filter that uses an object value
 * @returns - array of {@link PreFilterValue} objects
 */
export function mapParamFiltersToPreFilterValues(
    paramFilters: URLParamFilter[],
): PreFilterValue[] {
    const filterMap = paramFilters
        // Filter out any URL params that don't currently have a value - see item #1 in @remarks
        // comment above.
        .filter(
            ({ paramValue }: URLParamFilter): boolean => paramValue !== null,
        )
        .reduce(
            (
                acc: Map<string, PreFilterValue>,
                paramFilter: URLParamFilter,
            ): Map<string, PreFilterValue> => {
                // Write the paramValue to the specified filterValuePath.
                // filterValuePath is expected to begin with 'value'.
                const result = {
                    id: paramFilter.id,
                    value: null,
                };

                _set(
                    result,
                    paramFilter.filterValuePath,
                    paramFilter.paramValue,
                );

                const prev = acc.get(paramFilter.id);

                // Ensure that there is only one Filter per id. If an id is used twice,
                // merge its values together. See item #2 in the @remarks comment above.
                if (prev === undefined) {
                    acc.set(paramFilter.id, result);
                } else if (
                    typeof prev?.value === 'object' &&
                    typeof result?.value === 'object' &&
                    !Array.isArray(result?.value)
                ) {
                    acc.set(paramFilter.id, {
                        id: paramFilter.id,
                        value: Object.assign(prev?.value ?? {}, result.value),
                    });
                }

                return acc;
            },
            new Map<string, PreFilterValue>(),
        );

    return Array.from(filterMap.values());
}
